// Collection filter sidebar tools (merchant 2026-10-30, Baymard filtering research).
// <s-filter-list>: long value lists show the top 8 by product count (selected values always stay visible); "Show all (N)"
//   expands inline in alphabetical order and, past 10 values, reveals a find-in-list field. The expanded state is kept
//   per filter in sessionStorage because the live form re-renders the filter partial after every tick.
// [data-tip-toggle]: material "i" buttons open a one-line Glossary note under the row.
// <s-price-bands>: price band buttons fill the From / To fields and submit through the live form.
// <s-filters-toggle>: Nike-style "Hide filters" on desktop; remembered for the session.
const STORE = 'filter-list-expanded';

function readSet() {
  try { return new Set(JSON.parse(sessionStorage.getItem(STORE) || '[]')); } catch { return new Set(); }
}
function writeSet(set) {
  try { sessionStorage.setItem(STORE, JSON.stringify([...set])); } catch { /* storage blocked */ }
}

class FilterList extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key || '';
    this.toggle = this.querySelector('[data-list-toggle]');
    this.find = this.querySelector('[data-list-find]');
    this.dataset.ready = '';
    this.#set(readSet().has(this.key), false);
    this.toggle?.addEventListener('click', () => this.#set(!this.hasAttribute('data-expanded'), true));
    // The live form submits on any input event; the find field only filters this list.
    this.find?.addEventListener('input', (e) => { e.stopPropagation(); this.#filter(); });
    this.find?.addEventListener('change', (e) => e.stopPropagation());
    // Typing in the find field must not submit the live form.
    this.find?.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tip-toggle]');
      if (!btn) return;
      // The button sits inside the row's label: never let it tick the checkbox.
      e.preventDefault();
      const tip = document.getElementById(btn.getAttribute('aria-controls'));
      if (!tip) return;
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      tip.hidden = !open;
    });
  }

  #set(expanded, remember) {
    this.toggleAttribute('data-expanded', expanded);
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.toggle.textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
    }
    if (!expanded && this.find) {
      this.find.value = '';
      this.#filter();
    }
    if (remember) {
      const set = readSet();
      expanded ? set.add(this.key) : set.delete(this.key);
      writeSet(set);
    }
  }

  #filter() {
    const q = (this.find?.value || '').trim().toLowerCase();
    let shown = 0;
    this.querySelectorAll('li[data-label]').forEach((li) => {
      const hit = !q || li.dataset.label.includes(q);
      li.toggleAttribute('data-miss', !hit);
      if (hit) shown += 1;
    });
    const empty = this.querySelector('[data-list-empty]');
    if (empty) empty.hidden = shown > 0;
  }
}

class PriceBands extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gte], [data-lte]');
      if (!btn) return;
      const group = this.closest('details') || document;
      const gte = group.querySelector('input[name$=".gte"]');
      const lte = group.querySelector('input[name$=".lte"]');
      if (!gte || !lte) return;
      const active = btn.getAttribute('aria-pressed') === 'true';
      gte.value = active ? '' : btn.dataset.gte || '';
      lte.value = active ? '' : btn.dataset.lte || '';
      lte.dispatchEvent(new Event('input', { bubbles: true }));
      lte.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

const HIDE_KEY = 'collection-filters-hidden';
class FiltersToggle extends HTMLElement {
  connectedCallback() {
    this.layout = document.querySelector(this.dataset.target || '[data-collection-layout]');
    this.button = this.querySelector('button');
    if (!this.layout || !this.button) return;
    let hidden = false;
    try { hidden = sessionStorage.getItem(HIDE_KEY) === '1'; } catch { /* ignore */ }
    this.#apply(hidden);
    this.button.addEventListener('click', () => {
      const next = !this.layout.hasAttribute('data-filters-hidden');
      this.#apply(next);
      try { sessionStorage.setItem(HIDE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    });
  }

  #apply(hidden) {
    this.layout.toggleAttribute('data-filters-hidden', hidden);
    this.button.setAttribute('aria-pressed', String(!hidden));
    const text = hidden ? this.dataset.showLabel : this.dataset.hideLabel;
    this.button.setAttribute('aria-label', text);
    this.button.title = text;
    const label = this.button.querySelector('[data-label]');
    if (label) label.textContent = hidden ? this.dataset.showLabel : this.dataset.hideLabel;
  }
}

if (!customElements.get('s-filter-list')) customElements.define('s-filter-list', FilterList);
if (!customElements.get('s-price-bands')) customElements.define('s-price-bands', PriceBands);
if (!customElements.get('s-filters-toggle')) customElements.define('s-filters-toggle', FiltersToggle);

// <s-sort-menu>: desktop sort panel (listbox) that drives the hidden native select, so the live form submits as before.
class SortMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.panel = this.querySelector('[role="listbox"]');
    this.select = document.querySelector(this.dataset.select);
    if (!this.button || !this.panel || !this.select) return;
    this.button.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
    this.panel.addEventListener('click', (e) => {
      const opt = e.target.closest('[role="option"]');
      if (opt) this.choose(opt);
    });
    this.panel.addEventListener('keydown', (e) => this.#keys(e));
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); this.open(); }
    });
    this.onDoc = (e) => { if (!this.contains(e.target)) this.close(); };
  }

  disconnectedCallback() { document.removeEventListener('pointerdown', this.onDoc); }

  #options() { return [...this.panel.querySelectorAll('[role="option"]')]; }

  open() {
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    const current = this.panel.querySelector('[aria-selected="true"]') || this.#options()[0];
    current?.focus();
    document.addEventListener('pointerdown', this.onDoc);
  }

  close(focusButton = false) {
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onDoc);
    if (focusButton) this.button.focus();
  }

  choose(opt) {
    this.#options().forEach((o) => o.setAttribute('aria-selected', String(o === opt)));
    const current = this.querySelector('[data-sort-current]');
    if (current) current.textContent = opt.textContent.trim();
    this.close(true);
    if (this.select.value !== opt.dataset.value) {
      this.select.value = opt.dataset.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #keys(e) {
    const opts = this.#options();
    const i = opts.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); opts[Math.min(i + 1, opts.length - 1)]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); opts[Math.max(i - 1, 0)]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); opts[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1]?.focus(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (opts[i]) this.choose(opts[i]); }
    else if (e.key === 'Escape' || e.key === 'Tab') { this.close(e.key === 'Escape'); }
  }
}
if (!customElements.get('s-sort-menu')) customElements.define('s-sort-menu', SortMenu);
